/**
 * Financials Page - Owner Portal
 *
 * Purpose:
 *   Renders the Owner Financials view: period summary KPIs (Owner Payout, Guest Paid, Channel Fees,
 *   Tourist Tax), tabbed interface (Overview, Ledger, Channels, Payouts, Taxes), and detailed
 *   financial breakdowns. Data is fetched from GET /api/v1/owner/financials, scoped by
 *   selectedProperty.listing_id and ReportingPeriod range.
 *
 * Workflow:
 *   Session (selectedProperty) + ReportingPeriod (rangeStart, rangeEnd) -> fetch financials
 *   -> sanitize with effectiveMode -> destructure period_summary/rollups/reservations -> render
 *   KPIs and tabbed content (LedgerGrid, ChannelsPivotTable, TaxesLedger, PaymentStatus).
 *
 * Assumptions / Invariants:
 *   - Owner-only endpoint usage; no admin APIs or admin-only imports.
 *   - sanitizeForOwnerView(financialsData, effectiveMode) is always applied before
 *     destructuring or using financialsData in UI (Phase 1).
 *   - listingId must be a positive integer (or null); guards reject NaN, <=0, missing.
 *   - rangeStart / rangeEnd are Date instances from context; identity can churn
 *     when preset or custom range changes, which may trigger redundant refetches.
 *   - Financial math is display-only: backend provides pre-aggregated totals; no client-side
 *     tax aggregation or recomputation (Airbnb transient_occupancy_tax vs lodging_tax+sales_tax
 *     is handled by backend canonical summary).
 *   - AbortController cancels in-flight requests on unmount or dependency change (Phase 1).
 *   - Financial reconciliation checks run in debug mode only (?debug_financials=1) and are non-blocking (Phase 3).
 *
 * Dependencies:
 *   - react (useState, useEffect, useRef, useMemo, useCallback)
 *   - ownerApi.getFinancials (with optional AbortSignal support, Phase 1)
 *   - owner components: OwnerTabs, LedgerGrid, ChannelsPivotTable, TaxesLedger, PaymentStatus,
 *     OwnerPageChrome
 *   - utils: sanitizeForOwnerView, aggregatePaymentStatus, formatDateOnlyET, reconcileFinancials
 *   - contexts: useSession, useReportingPeriod
 *   - shared: SkeletonCard, SkeletonTable, ReportingPeriodBar
 *
 * Version: 1.3.0
 *
 * Changelog:
 *   - 1.3.0 (Phase 3): Added financial correctness reconciliation checks (debug mode only, gated by
 *     ?debug_financials=1). Non-blocking validation that compares period_summary totals vs reservation sums.
 *     No behavior changes to displayed values.
 *   - 1.2.0 (Phase 2): Added useMemo/useCallback for performance optimization (reportingPeriodRange,
 *     tabs array, handleExportXlsx, handleReservationClick, paymentStatus, propertyName, periodLabel, financialBreakdown).
 *     No behavior changes to displayed values.
 *   - 1.1.0 (Phase 1): Added charter-compliant header, sanitizeForOwnerView defense-in-depth,
 *     AbortController + mount guard for reliability. No behavior changes to displayed values.
 *   - 1.0.0: Initial Financials page (KPIs, tabbed interface, ledger/channels/taxes/payouts).
 */
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getFinancials, getFinancialsMonthsWithData, getFinancialsPaymentsTaxes, getReservationDetail, downloadFinancialsExportRange } from '../../services/ownerApi.js';
import { useSession } from '../../contexts/SessionContext.jsx';
import { useReportingPeriod } from '../../contexts/ReportingPeriodContext.jsx';
import { useViewport } from '../../contexts/ViewportContext.jsx';
import { SkeletonCard, SkeletonTable } from '../../components/shared/SkeletonLoader.jsx';
import ReportingPeriodBar from '../../components/shared/ReportingPeriodBar.jsx';
import FinancialEmptyState from '../../components/owner/FinancialEmptyState.jsx';
import OwnerTabs from '../../components/owner/OwnerTabs.jsx';
import LedgerGrid, { PRESETS as LEDGER_PRESETS, ALL_COLUMN_KEYS, COLUMN_REGISTRY } from '../../components/owner/LedgerGrid.jsx';
import TaxesLedger from '../../components/owner/TaxesLedger.jsx';
import PaymentStatus from '../../components/owner/PaymentStatus.jsx';
import OwnerPageChrome from '../../components/owner/OwnerPageChrome.jsx';
import FinancialsFiltersSheet from '../../components/owner/FinancialsFiltersSheet.jsx';
import BookingDetailModal from '../../components/owner/BookingDetailModal.jsx';
import Tooltip from '../../components/shared/Tooltip.jsx';
import LedgerHelpModal from '../../components/owner/LedgerHelpModal.jsx';
import { aggregatePaymentStatus } from '../../utils/aggregatePaymentStatus.js';
import { sanitizeForOwnerView } from '../../utils/sanitizeForOwnerView.js';
import { normalizeBookingForModal } from '../../utils/bookingDetailModalUtils.js';
import { reconcileFinancials } from '../../utils/financialsReconciliation.js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDateOnlyET } from '../../utils/dateTimeET.js';

// Payments & Taxes: lease detection — prefer backend is_lease, else master_channel_key
function isLeasePaymentsTaxes(row) {
  if (typeof row.is_lease === 'boolean') return row.is_lease;
  const m = (row.master_channel_key || '').toString().toLowerCase();
  if (m === 'clc_lease') return true;
  const s = (row.channel_subtype || '').toString().toLowerCase();
  return m === 'clc' && s === 'clc_lease';
}

// Add calendar days to a YYYY-MM-DD string; returns YYYY-MM-DD
function addDaysDateOnly(dateStr, days) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const d = new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10));
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Estimated payout date: non-lease = check-in + 7 days (e.g. "Jan 8, 2026"); lease = "—"
function estimatedPayoutDateDisplay(row) {
  if (isLeasePaymentsTaxes(row)) return '—';
  const plus7 = addDaysDateOnly(row.check_in, 7);
  if (!plus7) return '—';
  return formatDateOnlyET(plus7, { short: true });
}

// Payments & Taxes Channel: display ONLY from master_channel_key. Never show "Owner" unless key is explicitly 'owner'.
// If master_channel_key is missing/null, show "—" (conservative; never guess owner).
function paymentsTaxesChannelLabel(masterChannelKey) {
  const key = (masterChannelKey ?? '').toString().trim().toLowerCase();
  if (!key) return '—';
  if (key === 'airbnb') return 'Airbnb';
  if (key === 'vrbo') return 'VRBO';
  if (key === 'clc_lease') return 'Lease';
  if (key === 'clc') return 'CLC';
  if (key === 'owner') return 'Owner';
  return '—';
}

// Payments & Taxes table: single column definition for thead/tbody/tfoot alignment
const PAYMENTS_TAXES_COLUMNS = [
  { key: 'check_in', label: 'Check-in', numeric: false, tooltip: 'Reservation check-in date. Rows are ordered by this date.' },
  { key: 'check_out', label: 'Check-out', numeric: false },
  { key: 'guest', label: 'Guest', numeric: false },
  { key: 'channel', label: 'Channel', numeric: false, tooltip: 'Booking channel (e.g. Airbnb, VRBO).' },
  { key: 'estimated_payout_date', label: 'Estimated Payout Date', numeric: false, tooltip: 'Check-in date + 7 days for most channels. Leases use different terms (see note above).' },
  { key: 'owner_payout_gross', label: 'Owner Payout (Gross)', numeric: true, tooltip: 'Payout for this reservation before CLC fee and owner-remitted tax.' },
  { key: 'clc_fee', label: 'CLC Fee', numeric: true, tooltip: 'CLC management fee for this reservation.' },
  { key: 'state_tax_owner_remits', label: 'State Tax (Owner Remits)', numeric: true, tooltip: 'State tourist tax you remit for this reservation (channel-remitted excluded).' },
  { key: 'county_tax_owner_remits', label: 'County Tax (Owner Remits)', numeric: true, tooltip: 'County tourist tax you remit for this reservation (channel-remitted excluded).' },
  { key: 'net', label: 'Net', numeric: true, tooltip: 'Owner Payout minus CLC Fee minus State and County Tax (Owner Remits) for this reservation.' },
];

// Column widths (percent) for table-layout:fixed + colgroup so header/body stay aligned
const PAYMENTS_TAXES_COL_WIDTHS = {
  check_in: '8%',
  check_out: '8%',
  guest: '12%',
  channel: '8%',
  estimated_payout_date: '10%',
  owner_payout_gross: '11%',
  clc_fee: '9%',
  state_tax_owner_remits: '10%',
  county_tax_owner_remits: '10%',
  net: '11%',
};

// Map URL tab param to internal tab key (stable for deep-links)
function tabParamToKey(param) {
  if (param === 'payments-taxes') return 'payments_taxes';
  if (param === 'ledger') return 'ledger';
  if (param === 'overview') return 'overview';
  return 'overview';
}
function tabKeyToParam(key) {
  if (key === 'payments_taxes') return 'payments-taxes';
  return key;
}

export default function FinancialsPage({ onLogout }) {
  const { effectiveMode, selectedOwnerId, selectedPropertyId, selectedProperty } = useSession();
  const { rangeStart, rangeEnd, getResolvedLabel, setCustomRange } = useReportingPeriod();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [financialsData, setFinancialsData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => tabParamToKey(searchParams.get('tab')));
  const [exportError, setExportError] = useState(null);
  const [monthsWithData, setMonthsWithData] = useState([]);
  // Reservation view only; Guest view removed
  const [ledgerSearchQuery, setLedgerSearchQuery] = useState('');
  const [ledgerPresetId, setLedgerPresetId] = useState('owner-summary');
  const [ledgerColumnPickerOpen, setLedgerColumnPickerOpen] = useState(false);
  const [ledgerVisibleColumns, setLedgerVisibleColumns] = useState(() => new Set(LEDGER_PRESETS['owner-summary']?.columns ?? []));
  // Payments & Taxes tab: reservation-level data from clc_reservation_financials_fresh
  const [paymentsTaxesData, setPaymentsTaxesData] = useState(null);
  const [paymentsTaxesLoading, setPaymentsTaxesLoading] = useState(false);
  const [paymentsTaxesError, setPaymentsTaxesError] = useState(null);
  const [paymentsTaxesSummaryVisible, setPaymentsTaxesSummaryVisible] = useState(true);
  const [paymentsTaxesHideOwnerStays, setPaymentsTaxesHideOwnerStays] = useState(true);
  const [ptModalOpen, setPtModalOpen] = useState(false);
  const [ptModalBooking, setPtModalBooking] = useState(null);
  const [ptModalError, setPtModalError] = useState(null);
  // Start with check-in reporting; the optional by-night report is available in Help.
  // Do not restore a hidden reporting method from an older saved dropdown choice.
  const [ledgerMode, setLedgerMode] = useState('payment');
  // Ledger: Hide Owner Stays (default on); modal for guest name click (fetch-by-id like Reservations)
  const [ledgerHideOwnerStays, setLedgerHideOwnerStays] = useState(true);
  const [ledgerDetailOpen, setLedgerDetailOpen] = useState(false);
  const [ledgerDetailBooking, setLedgerDetailBooking] = useState(null);
  const [ledgerDetailError, setLedgerDetailError] = useState(null);
  const [ledgerHelpOpen, setLedgerHelpOpen] = useState(false);
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const ledgerColumnsButtonRef = useRef(null);
  const ledgerColumnPickerRef = useRef(null);
  const [columnPickerCoords, setColumnPickerCoords] = useState(null);
  // Removed expandedRows - no longer needed with new ledger design

  // Get property short name (matching Calendar/Reservations logic) - MOVED UP to avoid TDZ
  const getPropertyShortName = (property) => {
    return property?.display_name || property?.clc_short_name || property?.name || property?.property_name || 'Unnamed Property';
  };
  
  const propertyShortName = selectedProperty ? getPropertyShortName(selectedProperty) : 'Property';

  // Shared width constraint for all Financials page content - MOVED UP to avoid TDZ
  // Tighter horizontal spacing for more usable width (optimized for >= 1280px desktop)
  const contentWidthStyle = {
    maxWidth: '1600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    paddingLeft: 'var(--spacing-4, 16px)',
    paddingRight: 'var(--spacing-4, 16px)',
  };

  // Convert rangeStart/rangeEnd to YYYY-MM-DD strings for API calls
  const startDateStr = useMemo(() => {
    if (!rangeStart) return null;
    const y = rangeStart.getFullYear();
    const m = String(rangeStart.getMonth() + 1).padStart(2, '0');
    const d = String(rangeStart.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [rangeStart]);

  const endDateStr = useMemo(() => {
    if (!rangeEnd) return null;
    const y = rangeEnd.getFullYear();
    const m = String(rangeEnd.getMonth() + 1).padStart(2, '0');
    const d = String(rangeEnd.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [rangeEnd]);

  // Financials "Showing" label: formatted date range
  const financialsDisplayRange = useMemo(() => {
    if (!rangeStart || !rangeEnd) return null;
    const startFormatted = formatDateOnlyET(startDateStr, { short: true });
    const endDay = new Date(rangeEnd);
    endDay.setDate(endDay.getDate() - 1); // end is exclusive, show last day
    const endFormatted = formatDateOnlyET(endDay.toISOString().slice(0, 10), { short: true });
    return `${startFormatted} – ${endFormatted}`;
  }, [rangeStart, rangeEnd, startDateStr]);

  useEffect(() => {
    const abortController = new AbortController();
    let isActive = true;
    const listingId = selectedProperty?.listing_id;
    const listingIdNum = listingId != null ? (typeof listingId === 'string' ? parseInt(listingId, 10) : listingId) : null;

    // Defensive guards: ensure all required values are defined
    if (!listingIdNum || isNaN(listingIdNum) || listingIdNum <= 0) {
      if (isActive) {
        setLoading(false);
        setFinancialsData(null);
      }
      return undefined;
    }

    // Guard: date range must be defined
    if (!startDateStr || !endDateStr || !rangeStart || !rangeEnd) {
      if (isActive) {
        setLoading(false);
        setFinancialsData(null);
        setError(null); // Clear error if range is empty (user hasn't selected yet)
      }
      return undefined;
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const signal = abortController.signal;
        const groupBy = 'reservation';

        // The jump-to-month hint is optional; never delay the main figures for it.
        getFinancialsMonthsWithData({ listingId: listingIdNum, limit: 24, signal }).then(
          (monthsRes) => {
            if (!isActive) return;
            const monthsList = monthsRes?.data?.months ?? monthsRes?.months ?? [];
            setMonthsWithData(Array.isArray(monthsList) ? monthsList : []);
          },
          () => {
            if (isActive) setMonthsWithData([]);
          }
        );

        const financialsRes = await getFinancials({
          listingId: listingIdNum,
          startDate: startDateStr,
          endDate: endDateStr,
          groupBy,
          mode: ledgerMode,
          signal,
        });
        if (!isActive) return;
        const responseData = financialsRes?.data || financialsRes;
        const periodSummary = responseData?.period_summary || {};
        const reservationsMinimal = responseData?.reservations_minimal || [];
        const rollupsByMasterChannel = responseData?.rollups_by_master_channel || [];

        // Map API response to component state
        // For reservation view: map reservation rows
        // For guest summary view: map guest summary rows
        const channelLabel = (k) => (k === 'airbnb' ? 'Airbnb' : k === 'vrbo' ? 'VRBO' : k === 'clc' ? 'CLC' : k === 'owner' ? 'Owner' : k || '—');
        
        const mapRowsToGrid = (rows) => {
          // Reservation rows only (include display_channel for Ledger Hide Owner Stays; state_tax_total, county_tax_total for Ledger columns)
          return rows.map((r) => ({
              guest_name: r.guest_name,
              reservation_id: r.reservation_id,
              checkin_date: r.checkin_date || r.check_in,
              checkout_date: r.checkout_date || r.check_out,
              nights: r.nights || r.nights_in_range,
              guest_count: r.guest_count ?? null,
              master_channel_label: r.display_channel_label ?? channelLabel(r.master_channel_key),
              master_channel_key: r.master_channel_key,
              display_channel_key: r.display_channel_key,
              display_channel_label: r.display_channel_label,
              channel_subtype: r.channel_subtype,
              canonical_financial_summary: {
                guest_paid: {
                  guest_total_paid: 0, // Guest Paid removed
                  guest_base_rate: 0,
                  guest_cleaning_fee: 0,
                  guest_tourist_tax: Number(r.tourist_tax_total) || 0,
                },
                owner_payout: {
                  owner_payout_total: r.owner_payout_total != null ? Number(r.owner_payout_total) : null,
                  owner_channel_fees: Number(r.owner_channel_fees_total) || 0,
                  payout_status: 'pending',
                },
              },
              owner_base_rate_total: Number(r.owner_base_rate_total) || 0,
              ...(Object.hasOwn(r, 'owner_net_base_rate_total') ? {
                owner_net_base_rate_total: r.owner_net_base_rate_total,
                owner_gross_base_rate_total: r.owner_gross_base_rate_total,
                owner_discounts_total: r.owner_discounts_total,
                owner_discount_status: r.owner_discount_status,
              } : {}),
              owner_cleaning_fee_total: Number(r.owner_cleaning_fee_total) || 0,
              owner_channel_fees_total: Number(r.owner_channel_fees_total) || 0,
              owner_channel_fees_available: r.owner_channel_fees_available !== false,
              clcfee_total: Number(r.clcfee_total) || 0,
              tourist_tax_total: Number(r.tourist_tax_total) || 0,
              state_tax_total: Number(r.state_tax_total) || 0,
              county_tax_total: Number(r.county_tax_total) || 0,
              owner_revenue_total: Number(r.owner_revenue_total) || 0,
            }));
        };

        const reservations_minimal = mapRowsToGrid(reservationsMinimal);
        
        const rollups_by_master_channel = rollupsByMasterChannel.map((rollup) => ({
          master_channel_key: rollup.master_channel_key,
          master_channel_label: rollup.master_channel_label || channelLabel(rollup.master_channel_key),
          master_channel_color: rollup.master_channel_color,
          reservation_count: rollup.reservation_count || 0,
          nights: rollup.nights || 0,
          owner_payout_total: rollup.owner_payout_total ?? null,
          owner_channel_fees: rollup.owner_channel_fees || 0,
          guest_tourist_tax: rollup.tourist_tax || 0,
        }));

        const data = {
          period_summary: {
            ...periodSummary,
            tourist_tax: periodSummary.tourist_tax || periodSummary.tourist_tax_total || 0,
            guest_tourist_tax: periodSummary.tourist_tax || periodSummary.tourist_tax_total || 0,
          },
          reservations_minimal,
          rollups_by_master_channel,
        };
        setFinancialsData(sanitizeForOwnerView(data, effectiveMode));
      } catch (err) {
        if (err.name === 'AbortError' || abortController.signal.aborted) return;
        if (isActive) {
          console.error('[FinancialsPage] Error fetching financials:', err);
          setError(err.message || 'Failed to load financial data');
          setFinancialsData(null);
        }
      } finally {
        if (isActive) setLoading(false);
      }
    })();

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [startDateStr, endDateStr, selectedProperty?.listing_id, ledgerMode, effectiveMode]);

  // Payments & Taxes tab: fetch reservation-level data from clc_reservation_financials_fresh when tab is active
  useEffect(() => {
    if (activeTab !== 'payments_taxes') return;
    const listingId = selectedProperty?.listing_id;
    const listingIdNum = listingId != null ? (typeof listingId === 'string' ? parseInt(listingId, 10) : listingId) : null;
    if (!listingIdNum || isNaN(listingIdNum) || listingIdNum <= 0 || !startDateStr || !endDateStr) {
      setPaymentsTaxesData(null);
      setPaymentsTaxesError(null);
      return;
    }
    const ac = new AbortController();
    setPaymentsTaxesLoading(true);
    setPaymentsTaxesError(null);
    getFinancialsPaymentsTaxes({
      listingId: listingIdNum,
      startDate: startDateStr,
      endDate: endDateStr,
      signal: ac.signal,
    })
      .then((res) => {
        const data = res?.data ?? res;
        setPaymentsTaxesData({ rows: data?.rows ?? [], summary: data?.summary ?? {} });
        setPaymentsTaxesError(null);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setPaymentsTaxesData(null);
        setPaymentsTaxesError(err?.message || 'Failed to load payments and taxes');
      })
      .finally(() => setPaymentsTaxesLoading(false));
    return () => ac.abort();
  }, [activeTab, selectedProperty?.listing_id, startDateStr, endDateStr]);

  // Payments & Taxes: one-time console proof that scroll container is present (browser proof of new bundle)
  const ptScrollProofLogged = useRef(false);
  useEffect(() => {
    if (activeTab !== 'payments_taxes' || ptScrollProofLogged.current) return;
    const t = setTimeout(() => {
      const el = document.getElementById('payments-taxes-scroll-area');
      if (el) {
        ptScrollProofLogged.current = true;
        console.log('[P&T] scroll container mounted', { id: el.id, scrollTop: el.scrollTop, scrollHeight: el.scrollHeight, clientHeight: el.clientHeight, v: '20260130-single-scroll' });
      }
    }, 500);
    return () => clearTimeout(t);
  }, [activeTab]);

  // Deep-link: sync URL ?tab= to activeTab (back/forward and direct load)
  useEffect(() => {
    const param = searchParams.get('tab');
    const next = tabParamToKey(param);
    setActiveTab(next);
  }, [searchParams]);

  // Tab change handler: update state and URL so refresh keeps the tab
  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
    const param = tabKeyToParam(key);
    setSearchParams(param === 'overview' ? {} : { tab: param });
  }, [setSearchParams]);

  // Hero background state (like OwnerPageChrome but without large card)
  const { isMobile } = useViewport();
  const [heroImageReady, setHeroImageReady] = useState(false);
  const heroImageRef = useRef(null);
  
  const propertyHero = selectedProperty?.hero_url || null;
  const propertyThumb = selectedProperty?.thumb_url || null;

  useEffect(() => {
    if (!propertyHero) {
      setHeroImageReady(false);
      return;
    }
    setHeroImageReady(false);
    const img = new Image();
    img.onload = () => setHeroImageReady(true);
    img.onerror = () => setHeroImageReady(false);
    img.src = propertyHero;
    heroImageRef.current = img;
    return () => {
      if (heroImageRef.current) {
        heroImageRef.current.onload = null;
        heroImageRef.current.onerror = null;
      }
    };
  }, [propertyHero]);

  // HOTFIX: Extract data and call all hooks BEFORE early returns to fix React error #310
  // React error #310: "Rendered more hooks than during the previous render"
  // Root cause: Hooks were called AFTER conditional returns, causing inconsistent hook order
  // Fix: Move all hooks before any conditional returns to maintain consistent hook order on every render
  
  // Extract data from financials response (pre-aggregated by backend)
  const periodSummary = financialsData?.period_summary || {
    owner_payout_total: 0,
    tourist_tax: 0,
    guest_tourist_tax: 0,
    owner_channel_fees: 0,
    nights: 0,
    reservation_count: 0,
    occupancy_percent: 0,
  };

  const reservationsMinimal = financialsData?.reservations_minimal || [];

  const formatCurrency = (value) => {
    if (value == null || value === '' || !Number.isFinite(Number(value))) return 'Unavailable';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  // Use pre-aggregated period summary (no client-side calculation needed)
  const periodNights = periodSummary.nights || 0;
  const periodNet = periodSummary.owner_payout_total || 0;
  const periodDeposited = periodSummary.owner_payout_total || 0;
  const periodTaxesDue = periodSummary.tourist_tax || periodSummary.guest_tourist_tax || 0;
  const periodOccupancy = periodSummary.occupancy_percent ? periodSummary.occupancy_percent.toFixed(1) : '0.0';

  // Financial breakdown for Summary tab (from period_summary)
  // Phase 4: Guest Paid removed
  const financialBreakdown = useMemo(() => ({
    guestTouristTax: periodSummary.tourist_tax || periodSummary.guest_tourist_tax || 0,
    ownerPayoutTotal: periodSummary.owner_payout_total || 0,
    ownerChannelFees: periodSummary.owner_channel_fees || 0,
  }), [periodSummary]);

  // Aggregate payment status from reservations
  // Phase 2: Memoized to prevent redundant computation on every render
  const paymentStatus = useMemo(() => aggregatePaymentStatus(reservationsMinimal), [reservationsMinimal]);

  // Phase 3: Financial correctness reconciliation (debug mode only)
  // Non-blocking: never affects UI, never throws, gated by ?debug_financials=1
  const debugFinancials = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug_financials') === '1';
  const reconciliationReport = useMemo(() => {
    if (!debugFinancials || !financialsData) {
      return null;
    }
    try {
      return reconcileFinancials({
        period_summary: periodSummary,
        reservations_minimal: reservationsMinimal,
      });
    } catch (err) {
      // Non-blocking: log error but don't throw
      console.warn('[FinancialsPage] Reconciliation error (non-blocking):', err);
      return null;
    }
  }, [debugFinancials, financialsData, periodSummary, reservationsMinimal]);

  // Phase 3: Log reconciliation report in debug mode and attach to window for Playwright
  useEffect(() => {
    if (reconciliationReport && debugFinancials) {
      console.log('[FinancialsPage] Reconciliation Report:', reconciliationReport);
      
      // Attach to window for Playwright/testing
      if (typeof window !== 'undefined') {
        if (!window.__OWNER_DEBUG__) {
          window.__OWNER_DEBUG__ = {};
        }
        window.__OWNER_DEBUG__.financialsReconciliation = reconciliationReport;
      }
    }
  }, [reconciliationReport, debugFinancials]);

  const handleExportXlsx = useCallback(async () => {
    const listingId = selectedProperty?.listing_id;
    if (!listingId || !startDateStr || !endDateStr) return;
    setExportError(null);
    try {
      await downloadFinancialsExportRange({
        listingId,
        startDate: startDateStr,
        endDate: endDateStr,
        format: 'xlsx',
        groupBy: 'reservation',
        mode: ledgerMode,
      });
    } catch (e) {
      console.error('[FinancialsPage] Export XLSX failed:', e);
      setExportError(e.message || 'Excel export failed. Ensure exceljs is installed on the server.');
    }
  }, [selectedProperty?.listing_id, startDateStr, endDateStr, ledgerMode]);

  // Format Reporting Period date range for display: "Jan 1, 2026 – Jan 31, 2026" (inclusive month)
  const reportingPeriodRange = useMemo(() => {
    if (!rangeStart || !rangeEnd) return null;
    const startStr = `${rangeStart.getFullYear()}-${String(rangeStart.getMonth() + 1).padStart(2, '0')}-${String(rangeStart.getDate()).padStart(2, '0')}`;
    const endDay = new Date(rangeEnd);
    endDay.setDate(endDay.getDate() - 1); // end is exclusive, show last day of period
    const endStr = `${endDay.getFullYear()}-${String(endDay.getMonth() + 1).padStart(2, '0')}-${String(endDay.getDate()).padStart(2, '0')}`;
    const startFormatted = formatDateOnlyET(startStr, { short: true });
    const endFormatted = formatDateOnlyET(endStr, { short: true });
    return `${startFormatted} – ${endFormatted}`;
  }, [rangeStart, rangeEnd]);

  // Ledger: Guest name click opens BookingDetailModal (same as Reservations). Fetch by reservation_id so modal opens even if not in current list.
  const handleLedgerGuestClick = useCallback((reservation) => {
    const id = reservation?.reservation_id;
    if (!id) return;
    setLedgerDetailError(null);
    getReservationDetail(id)
      .then((res) => {
        const r = res?.reservation ?? res?.data?.reservation;
        if (!r) {
          setLedgerDetailError('Reservation not found');
          return;
        }
        const booking = {
          ...r,
          id: r.reservation_id,
          reservation_id: r.reservation_id,
          guest_name: r.guest_name,
          check_in_date: r.checkin_date ?? r.check_in_date,
          check_out_date: r.checkout_date ?? r.check_out_date,
          checkin_date: r.checkin_date ?? r.check_in_date,
          checkout_date: r.checkout_date ?? r.check_out_date,
          canonical_financial_summary: res?.canonical_financial_summary ?? res?.data?.canonical_financial_summary,
          financials_persisted_summary: res?.financials_persisted_summary ?? res?.data?.financials_persisted_summary,
        };
        setLedgerDetailBooking(booking);
        setLedgerDetailOpen(true);
      })
      .catch((err) => {
        setLedgerDetailError(err?.message || 'Failed to load reservation');
      });
  }, []);

  // HOTFIX ROUND 2: Move ALL hooks before early returns to fix React error #310
  // Violations found: useMemo hooks at lines 440, 495-496, 533-534 were AFTER early returns
  // These hooks must be called unconditionally before any early returns
  
  // Tabs array for OwnerTabs component (moved from line 440)
  const tabsArray = useMemo(() => [
    { key: 'overview', label: 'Overview' },
    { key: 'ledger', label: 'Ledger' },
    { key: 'payments_taxes', label: 'Payments & Taxes' },
  ], []);

  // Property name for LedgerGrid and TaxesLedger (moved from lines 495, 533)
  const ledgerPropertyName = useMemo(() => 
    selectedProperty?.display_name || selectedProperty?.clc_short_name || 'Property',
    [selectedProperty]
  );

  // Period label for LedgerGrid and TaxesLedger: month-only range when on Financials so it matches data
  const ledgerPeriodLabel = useMemo(() => financialsDisplayRange ?? getResolvedLabel(), [financialsDisplayRange, getResolvedLabel]);

  // Ledger: filter owner stays when "Hide Owner Stays" is checked (default). Use canonical (display_channel_key ?? master_channel_key), consistent with P&T.
  const filteredLedgerReservations = useMemo(() => {
    const rows = reservationsMinimal;
    if (!ledgerHideOwnerStays) return rows;
    return rows.filter((r) => {
      const key = (r.display_channel_key ?? r.master_channel_key ?? '').toString().trim().toLowerCase();
      return key !== 'owner';
    });
  }, [reservationsMinimal, ledgerHideOwnerStays]);

  // Payments & Taxes: filter owner stays when "Hide Owner Stays" is checked (default). Use canonical display_channel_key (consistent with channel labeling guardrail).
  const { paymentsTaxesFilteredRows, paymentsTaxesFilteredSummary } = useMemo(() => {
    const rows = paymentsTaxesData?.rows ?? [];
    if (!paymentsTaxesHideOwnerStays) {
      const payments_total = rows.reduce((s, r) => s + Number(r.owner_payout_total || 0), 0);
      const charges_total = rows.reduce((s, r) => s + Number(r.clcfee_total || 0) + Number(r.tourist_tax_owner_remits || 0), 0);
      return {
        paymentsTaxesFilteredRows: rows,
        paymentsTaxesFilteredSummary: {
          payments_total: Math.round(payments_total * 100) / 100,
          charges_total: Math.round(charges_total * 100) / 100,
          net_total: Math.round((payments_total - charges_total) * 100) / 100,
          reservation_count: rows.length,
          nights_total: rows.reduce((s, r) => s + Number(r.nights || 0), 0),
        },
      };
    }
    const filtered = rows.filter((r) => {
      const key = (r.display_channel_key ?? r.master_channel_key ?? '').toString().trim().toLowerCase();
      return key !== 'owner';
    });
    const payments_total = filtered.reduce((s, r) => s + Number(r.owner_payout_total || 0), 0);
    const charges_total = filtered.reduce((s, r) => s + Number(r.clcfee_total || 0) + Number(r.tourist_tax_owner_remits || 0), 0);
    return {
      paymentsTaxesFilteredRows: filtered,
      paymentsTaxesFilteredSummary: {
        payments_total: Math.round(payments_total * 100) / 100,
        charges_total: Math.round(charges_total * 100) / 100,
        net_total: Math.round((payments_total - charges_total) * 100) / 100,
        reservation_count: filtered.length,
        nights_total: filtered.reduce((s, r) => s + Number(r.nights || 0), 0),
      },
    };
  }, [paymentsTaxesData?.rows, paymentsTaxesHideOwnerStays]);

  // Payments & Taxes: open BookingDetailModal for guest (same modal as Calendar/Ledger). Fetch by reservation_id if needed.
  const handlePtGuestClick = useCallback((row) => {
    const id = row?.reservation_id;
    if (!id) return;
    setPtModalError(null);
    getReservationDetail(id)
      .then((res) => {
        const reservation = res?.reservation ?? res?.data?.reservation;
        if (!reservation) {
          setPtModalError('Reservation not found');
          return;
        }
        const booking = {
          ...reservation,
          id: reservation.reservation_id,
          reservation_id: reservation.reservation_id,
          guest_name: reservation.guest_name,
          check_in_date: reservation.checkin_date ?? reservation.check_in_date,
          check_out_date: reservation.checkout_date ?? reservation.check_out_date,
          checkin_date: reservation.checkin_date ?? reservation.check_in_date,
          checkout_date: reservation.checkout_date ?? reservation.check_out_date,
          canonical_financial_summary: res?.canonical_financial_summary ?? res?.data?.canonical_financial_summary,
          financials_persisted_summary: res?.financials_persisted_summary ?? res?.data?.financials_persisted_summary,
        };
        setPtModalBooking(booking);
        setPtModalOpen(true);
      })
      .catch((err) => {
        setPtModalError(err?.message || 'Failed to load reservation');
      });
  }, []);

  // Ledger preset/columns: sync visible columns when preset changes (reservation view only)
  useEffect(() => {
    const preset = LEDGER_PRESETS[ledgerPresetId];
    if (preset) setLedgerVisibleColumns(new Set(preset.columns));
  }, [ledgerPresetId]);

  // Bump when Owner Summary default columns change so existing browsers pick up new defaults (e.g. amount_deposited).
  const OWNER_SUMMARY_COLUMNS_VERSION = '2';
  useEffect(() => {
    if (ledgerPresetId !== 'owner-summary') return;
    try {
      const key = 'owner_financials_owner_summary_cols_v';
      if (localStorage.getItem(key) === OWNER_SUMMARY_COLUMNS_VERSION) return;
      localStorage.setItem(key, OWNER_SUMMARY_COLUMNS_VERSION);
      setLedgerVisibleColumns(new Set(LEDGER_PRESETS['owner-summary'].columns));
    } catch {
      // ignore quota
    }
  }, [ledgerPresetId]);

  const toggleLedgerColumn = useCallback((key) => {
    setLedgerVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Column picker: position via portal (avoids clipping when few rows)
  useLayoutEffect(() => {
    if (!ledgerColumnPickerOpen || !ledgerColumnsButtonRef.current) {
      setColumnPickerCoords(null);
      return;
    }
    const rect = ledgerColumnsButtonRef.current.getBoundingClientRect();
    const maxH = 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const flip = spaceBelow < maxH && rect.top > spaceBelow;
    const minW = 180;
    setColumnPickerCoords({
      top: flip ? rect.top - maxH - 4 : rect.bottom + 4,
      left: Math.max(8, rect.right - minW),
      flip,
    });
  }, [ledgerColumnPickerOpen]);

  // Column picker: close on click outside
  useEffect(() => {
    if (!ledgerColumnPickerOpen) return;
    const handleClickOutside = (e) => {
      const btn = ledgerColumnsButtonRef.current;
      const popup = ledgerColumnPickerRef.current;
      if (btn && popup && !btn.contains(e.target) && !popup.contains(e.target)) {
        setLedgerColumnPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ledgerColumnPickerOpen]);

  // Column picker: close on Esc
  useEffect(() => {
    if (!ledgerColumnPickerOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setLedgerColumnPickerOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [ledgerColumnPickerOpen]);

  const ledgerActivePresets = LEDGER_PRESETS;

  // HOTFIX: Early returns moved AFTER all hooks to fix React error #310
  // All hooks must be called in the same order on every render
  if (loading) {
    return (
      <OwnerPageChrome
        title={propertyShortName}
        subtitle="Financial reporting and ledger"
        compactLayout={isMobile}
        headerMaxWidth="1600px"
      >
        <div style={contentWidthStyle}>
          <SkeletonCard />
          <div style={{ marginTop: 'var(--spacing-6)' }}>
            <SkeletonTable rows={5} cols={4} />
          </div>
        </div>
      </OwnerPageChrome>
    );
  }

  if (error && !financialsData) {
    return (
      <OwnerPageChrome
        title={propertyShortName}
        subtitle="Financial reporting and ledger"
        compactLayout={isMobile}
        headerMaxWidth="1600px"
      >
        <div style={contentWidthStyle}>
          <div className="owner-alert owner-alert-error">
            {error || 'Failed to load financial data'}
          </div>
        </div>
      </OwnerPageChrome>
    );
  }

  // Mobile: single-column layout for Overview pillars/context and P&T summary strip
  const overviewPillarsStyle = isMobile ? { ...styles.overviewPillars, gridTemplateColumns: '1fr' } : styles.overviewPillars;
  const overviewContextStyle = isMobile ? { ...styles.overviewContext, gridTemplateColumns: '1fr' } : styles.overviewContext;
  const paymentsTaxesSummaryStripStyle = isMobile ? { ...styles.paymentsTaxesSummaryStrip, gridTemplateColumns: '1fr' } : styles.paymentsTaxesSummaryStrip;

  return (
    <OwnerPageChrome
      title={propertyShortName}
      subtitle="Financial reporting and ledger"
      compactLayout={isMobile}
      headerMaxWidth="1600px"
      // Keep Financials on natural page scroll. Constrained mode (`height: 100vh` + `overflow: hidden`)
      // at the page chrome level can clip ledger content for large datasets on some viewports.
      contentConstrained={false}
      headerTopRightSlot={isMobile ? (
        <button
          type="button"
          onClick={() => setFiltersSheetOpen(true)}
          style={styles.filtersButton}
          aria-label="Open filters"
        >
          Filters
        </button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3, 12px)', width: '100%', maxWidth: 520, justifyContent: 'flex-end' }}>
          <input
            type="text"
            placeholder="Search ledger by guest, reservation ID, or channel…"
            value={ledgerSearchQuery}
            onChange={(e) => setLedgerSearchQuery(e.target.value)}
            style={{
              flex: 1,
              minWidth: 300,
              maxWidth: 360,
              padding: 'var(--spacing-2, 8px) var(--spacing-3, 12px)',
              fontSize: 'var(--font-size-sm, 14px)',
              border: '1px solid var(--color-border-light, #e5e7eb)',
              borderRadius: 'var(--radius-md, 6px)',
              backgroundColor: 'var(--color-bg-primary, #fff)',
            }}
            aria-label="Search ledger"
          />
          <div style={{ flexShrink: 0 }}>
            {exportError && (
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error, #dc2626)', marginRight: 8 }}>
                {exportError}
              </span>
            )}
            <button
              type="button"
              onClick={() => { setExportError(null); handleExportXlsx(); }}
              style={{
                padding: 'var(--spacing-2, 8px) var(--spacing-3, 12px)',
                fontSize: 'var(--font-size-sm, 14px)',
                fontWeight: 'var(--font-weight-medium, 500)',
                color: 'var(--color-text-primary, #111827)',
                backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
                border: '1px solid var(--color-border-light, #e5e7eb)',
                borderRadius: 'var(--radius-md, 6px)',
                cursor: 'pointer',
              }}
              aria-label="Export ledger to Excel"
            >
              Export to Excel
            </button>
          </div>
        </div>
      )}
    >
      <div data-page="financials" style={{ width: '100%', minWidth: 0 }}>
      {/* Ledger Sheet - When Ledger active, flex to fill viewport with inner scroll. When P&T active, natural height so page scrolls. */}
      <div style={{
        ...styles.ledgerSheet,
        ...(activeTab === 'ledger' ? {
          flex: 1,
          minHeight: 0,
          // Must not be `hidden`: nested overflow:hidden creates extra scrollports; LedgerGrid thead sticky
          // then sticks to this box top and overlaps ledgerHeaderLayout (tabs/toolbar).
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
        } : {}),
        ...(activeTab === 'payments_taxes' ? { minHeight: 0 } : {}),
      }}>
          {/* Compact 2-row header: Row1 = period+showing | view; Row2 = tabs | preset+columns */}
          <div style={styles.ledgerHeaderLayout}>
            {/* Row 1: period + showing (mobile: single tap opens Filters sheet) */}
            <div style={styles.ledgerHeaderRow1}>
              <div style={styles.ledgerHeaderRow1Left}>
                {isMobile ? (
                  <button
                    type="button"
                    onClick={() => setFiltersSheetOpen(true)}
                    style={styles.periodButtonMobile}
                    aria-label="Change reporting period and filters"
                  >
                    Showing: {financialsDisplayRange ?? reportingPeriodRange ?? 'Select period'}
                  </button>
                ) : (
                  <>
                    <ReportingPeriodBar labelFontSize="var(--font-size-base, 16px)" />
                    {(financialsDisplayRange ?? reportingPeriodRange) && (
                      <span style={styles.dateRangeDisplay}>
                        Showing: {financialsDisplayRange ?? reportingPeriodRange}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            {/* Row 2: LEFT tabs (ledger-specific modern strip), RIGHT Preset + Columns or Hide Owner Stays (P&T) */}
            <div style={styles.ledgerHeaderRow2}>
              <OwnerTabs
                activeKey={activeTab}
                onChange={handleTabChange}
                tabs={tabsArray}
                variant="ledger"
              />
              {activeTab === 'payments_taxes' && (
                <div style={styles.paymentsTaxesHeaderRight}>
                  <label style={styles.paymentsTaxesCheckboxLabel}>
                    <input
                      type="checkbox"
                      checked={paymentsTaxesHideOwnerStays}
                      onChange={(e) => setPaymentsTaxesHideOwnerStays(e.target.checked)}
                      aria-label="Hide Owner Stays"
                    />
                    <span>Hide Owner Stays</span>
                  </label>
                </div>
              )}
              {activeTab === 'ledger' && (
                <div style={styles.ledgerHeaderRow2Right}>
                  {ledgerMode === 'accrual' && (
                    <span style={styles.presetLabel} role="status">
                      By-night report ·{' '}
                      <button type="button" onClick={() => setLedgerMode('payment')}>
                        Return to check-in view
                      </button>
                    </span>
                  )}
                  <button
                    type="button"
                    className="ledger-help-button"
                    onClick={() => setLedgerHelpOpen(true)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      minHeight: '32px',
                      padding: '4px 10px',
                      background: 'none',
                      border: 'none',
                      borderRadius: 'var(--radius-md, 6px)',
                      fontSize: 'var(--font-size-sm, 14px)',
                      fontWeight: 'var(--font-weight-medium, 500)',
                      color: 'var(--color-link, #2563eb)',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                    aria-label="How your ledger totals work"
                  >
                    <span style={{ fontSize: '16px', lineHeight: 1, opacity: 0.9 }} aria-hidden="true">ⓘ</span>
                    <span>Help</span>
                  </button>
                  {ledgerHelpOpen && (
                    <LedgerHelpModal
                      mode={ledgerMode}
                      onModeChange={setLedgerMode}
                      onClose={() => setLedgerHelpOpen(false)}
                    />
                  )}
                  <label style={styles.presetLabel} htmlFor="ledger-preset">Preset:</label>
                  <select
                    id="ledger-preset"
                    value={ledgerPresetId}
                    onChange={(e) => setLedgerPresetId(e.target.value)}
                    style={styles.presetSelect}
                    aria-label="Column preset"
                  >
                    {Object.entries(ledgerActivePresets).map(([id, p]) => (
                      <option key={id} value={id}>{p.label}</option>
                    ))}
                  </select>
                  <div style={{ position: 'relative' }}>
                    <button
                      ref={ledgerColumnsButtonRef}
                      type="button"
                      onClick={() => setLedgerColumnPickerOpen((o) => !o)}
                      style={styles.columnsButton}
                      aria-label="Choose columns"
                      aria-expanded={ledgerColumnPickerOpen}
                    >
                      Columns
                    </button>
                    {ledgerColumnPickerOpen && columnPickerCoords && createPortal(
                      <div
                        ref={ledgerColumnPickerRef}
                        role="dialog"
                        aria-label="Column picker"
                        style={{
                          ...styles.columnPickerDropdown,
                          position: 'fixed',
                          top: columnPickerCoords.top,
                          left: columnPickerCoords.left,
                          right: 'auto',
                          marginTop: 0,
                          minWidth: 180,
                          maxHeight: 320,
                          overflowY: 'auto',
                          zIndex: 10000,
                        }}
                      >
                        {ALL_COLUMN_KEYS.filter((key) => {
                          if (key === 'guest_paid') return false;
                          const col = COLUMN_REGISTRY[key];
                          if (!col) return false;
                          return col.reservationView === true;
                        }).map((key) => {
                          const col = COLUMN_REGISTRY[key];
                          const isPlaceholder = col?.placeholder === true;
                          return (
                            <label
                              key={key}
                              style={{ ...styles.columnPickerLabel, cursor: isPlaceholder ? 'not-allowed' : 'pointer', opacity: isPlaceholder ? 0.6 : 1 }}
                              title={isPlaceholder ? 'Coming soon' : undefined}
                            >
                              <input
                                type="checkbox"
                                checked={ledgerVisibleColumns.has(key)}
                                onChange={() => !isPlaceholder && toggleLedgerColumn(key)}
                                disabled={isPlaceholder}
                              />
                              <span style={{ fontSize: 'var(--font-size-sm)' }}>
                                {col?.label ?? key}
                                {isPlaceholder && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginLeft: 4 }}>(soon)</span>}
                              </span>
                            </label>
                          );
                        })}
                      </div>,
                      document.body
                    )}
                  </div>
                  <label style={styles.paymentsTaxesCheckboxLabel}>
                    <input
                      type="checkbox"
                      checked={ledgerHideOwnerStays}
                      onChange={(e) => setLedgerHideOwnerStays(e.target.checked)}
                      aria-label="Hide Owner Stays"
                    />
                    <span>Hide Owner Stays</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Tab Content - When Ledger active, flex so scroll is inside ledger. P&T uses natural height + page scroll. */}
          <div style={{
            ...styles.tabContentContainer,
            ...(activeTab === 'ledger' ? { flex: 1, minHeight: 0, overflow: 'visible', display: 'flex', flexDirection: 'column' } : {}),
          }}>
          {/* Overview Tab — Hero Period Summary (canonical owner-centric; tooltips on every item) */}
          {activeTab === 'overview' && (
            <div style={styles.tabContent}>
              <div style={styles.overviewCard}>
                <h2 style={styles.overviewTitle}>Period Summary</h2>
                {/* Tier 1: Hero — Owner Revenue (Net) */}
                <div style={styles.overviewHero}>
                  <div style={styles.overviewHeroLabelRow}>
                    <span style={styles.overviewHeroLabel}>Owner Revenue (Net)</span>
                    <Tooltip content="The amount you keep after CLC fees and any taxes you remit directly. This is your true earnings for the selected period." asButton={true}>
                      <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                    </Tooltip>
                  </div>
                  <div style={styles.overviewHeroValue}>{formatCurrency(periodSummary.owner_revenue_total ?? 0)}</div>
                  <div style={styles.overviewHeroSubtext}>Your earnings after fees and taxes for the selected period.</div>
                </div>
                {/* Tier 2: Supporting pillars */}
                <div style={overviewPillarsStyle}>
                  <div style={styles.overviewPillarItem}>
                    <div style={styles.overviewPillarLabelRow}>
                      <span>Owner Payout (Gross)</span>
                      <Tooltip content="Total payout generated from reservations before CLC fees and owner-remitted taxes." asButton={true}>
                        <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                      </Tooltip>
                    </div>
                    <div style={styles.overviewPillarValue}>{formatCurrency(periodSummary.owner_payout_total)}</div>
                  </div>
                  <div style={styles.overviewPillarItem}>
                    <div style={styles.overviewPillarLabelRow}>
                      <span>CLC Fees</span>
                      <Tooltip content="Management fees charged by CLC for the selected period." asButton={true}>
                        <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                      </Tooltip>
                    </div>
                    <div style={styles.overviewPillarValue}>{formatCurrency(periodSummary.clcfee_total ?? 0)}</div>
                  </div>
                  <div style={styles.overviewPillarItem}>
                    <div style={styles.overviewPillarLabelRow}>
                      <span>Channel Fees</span>
                      <Tooltip content="Fees charged by booking channels that reduce your payout." asButton={true}>
                        <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                      </Tooltip>
                    </div>
                    <div style={styles.overviewPillarValue}>{formatCurrency(Math.abs(Number(periodSummary.owner_channel_fees) || 0))}</div>
                  </div>
                </div>
                {/* Tier 3: Context */}
                <div style={overviewContextStyle}>
                  <div style={styles.overviewContextItem}>
                    <div style={styles.overviewContextLabelRow}>
                      <span>Tourist Tax (Owner Remits)</span>
                      <Tooltip content="Taxes collected from guests that you are responsible for remitting to local authorities." asButton={true}>
                        <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                      </Tooltip>
                    </div>
                    <div style={styles.overviewContextValue}>{formatCurrency(periodSummary.tourist_tax ?? periodSummary.guest_tourist_tax ?? 0)}</div>
                  </div>
                  <div style={styles.overviewContextItem}>
                    <div style={styles.overviewContextLabelRow}>
                      <span>Reservations</span>
                      <Tooltip content="Number of completed reservations included in the selected period." asButton={true}>
                        <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                      </Tooltip>
                    </div>
                    <div style={styles.overviewContextValue}>{periodSummary.reservation_count ?? 0}</div>
                  </div>
                  <div style={styles.overviewContextItem}>
                    <div style={styles.overviewContextLabelRow}>
                      <span>Nights</span>
                      <Tooltip content="Total occupied nights across included reservations in the selected period." asButton={true}>
                        <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                      </Tooltip>
                    </div>
                    <div style={styles.overviewContextValue}>{periodSummary.nights ?? 0}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Ledger Tab - Primary Working Surface (View/Preset/Columns in header) */}
          {activeTab === 'ledger' && (
            <div style={{ ...styles.tabContent, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
              {reservationsMinimal.length === 0 ? (
                <FinancialEmptyState
                  type="no_reservations"
                  monthLabel={financialsDisplayRange}
                  jumpTargetLabel={monthsWithData.length > 0 ? new Date(monthsWithData[0] + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : null}
                  onJumpToMonth={monthsWithData.length > 0 ? () => {
                    const mk = monthsWithData[0];
                    const [y, m] = mk.split('-').map(Number);
                    setCustomRange(new Date(y, m - 1, 1), new Date(y, m, 1));
                  } : undefined}
                />
              ) : (
                <LedgerGrid
                  reservations={filteredLedgerReservations}
                  onRowClick={null}
                  onGuestNameClick={handleLedgerGuestClick}
                  onExportCSV={null}
                  propertyName={ledgerPropertyName}
                  periodLabel={ledgerPeriodLabel}
                  viewMode="reservation"
                  searchQuery={ledgerSearchQuery}
                  hideToolbar={true}
                  presetId={ledgerPresetId}
                  onPresetChange={setLedgerPresetId}
                  visibleColumns={ledgerVisibleColumns}
                  onToggleColumn={toggleLedgerColumn}
                  channelFeesSomeUnavailable={periodSummary?.channel_fees_some_unavailable === true}
                />
              )}
            </div>
          )}

          {/* Payments & Taxes Tab — reservation-level from clc_reservation_financials_fresh (check-in anchored). No flex/scroll trap: table expands naturally, page scrolls. */}
          {activeTab === 'payments_taxes' && (
            <div style={styles.tabContent}>
              <section style={styles.paymentsTaxesSection}>
                {paymentsTaxesLoading ? (
                  <>
                    <h2 style={styles.paymentsTaxesCardTitle}>Payments & Taxes</h2>
                    <div style={styles.paymentsTaxesContentWrap}>
                      <SkeletonTable rows={6} cols={8} />
                    </div>
                  </>
                ) : paymentsTaxesError ? (
                  <>
                    <h2 style={styles.paymentsTaxesCardTitle}>Payments & Taxes</h2>
                    <div style={styles.paymentsTaxesErrorWrap}>
                      <div className="owner-alert owner-alert-error" role="alert">{paymentsTaxesError}</div>
                    </div>
                  </>
                ) : !paymentsTaxesData?.rows?.length ? (
                  <>
                    <h2 style={styles.paymentsTaxesCardTitle}>Payments & Taxes</h2>
                    <div style={styles.paymentsTaxesContentWrap}>
                      <FinancialEmptyState
                      type="no_reservations"
                      monthLabel={financialsDisplayRange}
                      jumpTargetLabel={monthsWithData.length > 0 ? new Date(monthsWithData[0] + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : null}
                      onJumpToMonth={monthsWithData.length > 0 ? () => {
                        const mk = monthsWithData[0];
                        const [y, m] = mk.split('-').map(Number);
                        setCustomRange(new Date(y, m - 1, 1), new Date(y, m, 1));
                      } : undefined}
                    />
                    </div>
                  </>
                ) : (
                  <>
                    {ptModalError && (
                      <div className="owner-alert owner-alert-error" style={{ marginBottom: 'var(--spacing-2)' }} role="alert">
                        {ptModalError}
                        <button type="button" onClick={() => setPtModalError(null)} style={{ marginLeft: 'var(--spacing-2)' }}>Dismiss</button>
                      </div>
                    )}
                    <div style={styles.paymentsTaxesTitleRow}>
                      <h2 style={styles.paymentsTaxesCardTitle}>Payments & Taxes</h2>
                      <button
                        type="button"
                        onClick={() => setPaymentsTaxesSummaryVisible((v) => !v)}
                        style={styles.paymentsTaxesSummaryToggle}
                        aria-expanded={paymentsTaxesSummaryVisible}
                        aria-label={paymentsTaxesSummaryVisible ? 'Hide summary' : 'Show summary'}
                      >
                        {paymentsTaxesSummaryVisible ? 'Hide summary' : 'Show summary'}
                      </button>
                    </div>
                    <div style={{ ...styles.paymentsTaxesSummaryCollapse, maxHeight: paymentsTaxesSummaryVisible ? 400 : 0, opacity: paymentsTaxesSummaryVisible ? 1 : 0, marginBottom: paymentsTaxesSummaryVisible ? undefined : 0, paddingBottom: paymentsTaxesSummaryVisible ? undefined : 0 }}>
                      <div style={paymentsTaxesSummaryStripStyle}>
                          <div style={styles.paymentsTaxesSummaryItem}>
                            <div style={styles.paymentsTaxesSummaryLabelRow}>
                              <span>Payments (Owner Payout Gross)</span>
                              <Tooltip content="Total payout to you from reservations in the period (before CLC fees and owner-remitted taxes)." asButton={true}>
                                <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                              </Tooltip>
                            </div>
                            <div style={styles.paymentsTaxesSummaryValue}>{formatCurrency(paymentsTaxesFilteredSummary.payments_total ?? 0)}</div>
                          </div>
                          <div style={styles.paymentsTaxesSummaryItem}>
                            <div style={styles.paymentsTaxesSummaryLabelRow}>
                              <span>Charges (CLC Fee + State & County Tax Owner Remits)</span>
                              <Tooltip content="CLC management fees plus state and county tourist tax you remit to authorities (excludes channel-remitted tax)." asButton={true}>
                                <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                              </Tooltip>
                            </div>
                            <div style={styles.paymentsTaxesSummaryValue}>{formatCurrency(paymentsTaxesFilteredSummary.charges_total ?? 0)}</div>
                          </div>
                          <div style={styles.paymentsTaxesSummaryItem}>
                            <div style={styles.paymentsTaxesSummaryLabelRow}>
                              <span>Net</span>
                              <Tooltip content="Payments minus charges: your net cash for the period." asButton={true}>
                                <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                              </Tooltip>
                            </div>
                            <div style={styles.paymentsTaxesSummaryValue}>{formatCurrency(paymentsTaxesFilteredSummary.net_total ?? 0)}</div>
                          </div>
                          <div style={styles.paymentsTaxesSummaryItem}>
                            <div style={styles.paymentsTaxesSummaryLabelRow}>
                              <span>Reservations</span>
                              <Tooltip content="Number of reservations with check-in in the selected period (cancelled/inquiry/owner stays excluded when hidden)." asButton={true}>
                                <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                              </Tooltip>
                            </div>
                            <div style={styles.paymentsTaxesSummaryValue}>{paymentsTaxesFilteredSummary.reservation_count ?? 0}</div>
                          </div>
                          <div style={styles.paymentsTaxesSummaryItem}>
                            <div style={styles.paymentsTaxesSummaryLabelRow}>
                              <span>Nights</span>
                              <Tooltip content="Total occupied nights across included reservations." asButton={true}>
                                <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                              </Tooltip>
                            </div>
                            <div style={styles.paymentsTaxesSummaryValue}>{paymentsTaxesFilteredSummary.nights_total ?? 0}</div>
                          </div>
                        </div>
                        <p style={styles.paymentsTaxesHelperCopy}>
                          <em>All amounts are calculated based on the reservation check-in date.</em>
                        </p>
                        <p style={styles.paymentsTaxesHelperCopy}>
                          <em>Estimated payout. Funds are released after check-in and typically deposited within 3–7 days. Contact Crissy if funds have not been received.</em>
                        </p>
                        <p style={styles.paymentsTaxesHelperCopy}>
                          <em>Lease payout details. Typically 50% upon signing and 50% 30–60 days prior to check-in.</em>
                        </p>
                      </div>
                    <div
                      id="payments-taxes-scroll-area"
                      data-scroll-container="payments-taxes"
                      role="region"
                      aria-label="Payments and taxes table scroll area"
                      style={styles.paymentsTaxesScrollArea}
                    >
                      {paymentsTaxesFilteredRows.length === 0 ? (
                      <div style={styles.paymentsTaxesContentWrap}>
                        <p style={{ color: 'var(--color-text-secondary)', margin: 'var(--spacing-4)' }}>No reservations to show. Try unchecking “Hide Owner Stays” or change the date range.</p>
                      </div>
                    ) : (
                      <div style={styles.paymentsTaxesTableScrollWrap}>
                        <div style={styles.paymentsTaxesTableWrap}>
                          <table style={styles.paymentsTaxesTable} role="grid" aria-label="Payments and taxes by reservation">
                            <colgroup>
                              {PAYMENTS_TAXES_COLUMNS.map((col) => (
                                <col key={col.key} style={{ width: PAYMENTS_TAXES_COL_WIDTHS[col.key] }} />
                              ))}
                            </colgroup>
                            <thead style={styles.paymentsTaxesTheadSticky}>
                              <tr>
                                {PAYMENTS_TAXES_COLUMNS.map((col) => {
                                  const ThStyle = col.numeric ? { ...styles.paymentsTaxesThNumeric, ...styles.paymentsTaxesThSticky, ...styles.paymentsTaxesThHierarchy } : { ...styles.paymentsTaxesTh, ...styles.paymentsTaxesThSticky, ...styles.paymentsTaxesThHierarchy };
                                  return (
                                    <th key={col.key} style={ThStyle}>
                                      <span style={styles.paymentsTaxesThInner}>
                                        {col.label}
                                        {col.tooltip && (
                                          <Tooltip content={col.tooltip} asButton={true}>
                                            <span style={styles.overviewTooltipIcon} aria-hidden="true">ⓘ</span>
                                          </Tooltip>
                                        )}
                                      </span>
                                    </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              {paymentsTaxesFilteredRows.map((row) => {
                                const cellValue = (col) => {
                                  switch (col.key) {
                                    case 'check_in': return formatDateOnlyET(row.check_in, { short: true }) ?? '—';
                                    case 'check_out': return formatDateOnlyET(row.check_out, { short: true }) ?? '—';
                                    case 'guest':
                                      return (
                                        <button
                                          type="button"
                                          onClick={() => handlePtGuestClick(row)}
                                          style={styles.paymentsTaxesGuestButton}
                                          className="pt-guest-button"
                                          aria-label={`View reservation ${row.reservation_id} details`}
                                        >
                                          {row.guest_name || '—'}
                                        </button>
                                      );
                                    case 'channel': return (row.display_channel_label != null && row.display_channel_label !== '') ? row.display_channel_label : paymentsTaxesChannelLabel(row.master_channel_key);
                                    case 'estimated_payout_date': return estimatedPayoutDateDisplay(row);
                                    case 'owner_payout_gross': return formatCurrency(Number(row.owner_payout_total) || 0);
                                    case 'clc_fee': return formatCurrency(Number(row.clcfee_total) || 0);
                                    case 'state_tax_owner_remits': return formatCurrency(Number(row.state_tax_owner_remits) || 0);
                                    case 'county_tax_owner_remits': return formatCurrency(Number(row.county_tax_owner_remits) || 0);
                                    case 'net': return formatCurrency(Number(row.net_total) || 0);
                                    default: return '—';
                                  }
                                };
                                return (
                                  <tr key={row.reservation_id || row.check_in + (row.guest_name || '')}>
                                    {PAYMENTS_TAXES_COLUMNS.map((col) => {
                                      const TdStyle = col.numeric ? styles.paymentsTaxesTdNumeric : styles.paymentsTaxesTd;
                                      return <td key={col.key} style={TdStyle}>{cellValue(col)}</td>;
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr style={styles.paymentsTaxesTotalsRow}>
                                <td style={styles.paymentsTaxesTd} colSpan={5}><strong>Totals</strong></td>
                                <td style={styles.paymentsTaxesTdNumeric}>{formatCurrency(paymentsTaxesFilteredSummary.payments_total ?? 0)}</td>
                                <td style={styles.paymentsTaxesTdNumeric}>{formatCurrency(paymentsTaxesFilteredRows.reduce((s, r) => s + (Number(r.clcfee_total) || 0), 0))}</td>
                                <td style={styles.paymentsTaxesTdNumeric}>{formatCurrency(paymentsTaxesFilteredRows.reduce((s, r) => s + (Number(r.state_tax_owner_remits) || 0), 0))}</td>
                                <td style={styles.paymentsTaxesTdNumeric}>{formatCurrency(paymentsTaxesFilteredRows.reduce((s, r) => s + (Number(r.county_tax_owner_remits) || 0), 0))}</td>
                                <td style={styles.paymentsTaxesTdNumeric}>{formatCurrency(paymentsTaxesFilteredSummary.net_total ?? 0)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                      )}
                    </div>
                  </>
                )}
              </section>
            </div>
          )}
          </div>
        </div>
      </div>
      <BookingDetailModal
        isOpen={ptModalOpen}
        booking={ptModalBooking ? normalizeBookingForModal(sanitizeForOwnerView(ptModalBooking, effectiveMode), selectedProperty?.listing_id) : null}
        onClose={() => { setPtModalOpen(false); setPtModalBooking(null); setPtModalError(null); }}
      />
      <BookingDetailModal
        isOpen={ledgerDetailOpen}
        booking={ledgerDetailBooking ? normalizeBookingForModal(sanitizeForOwnerView(ledgerDetailBooking, effectiveMode), selectedProperty?.listing_id) : null}
        onClose={() => { setLedgerDetailOpen(false); setLedgerDetailBooking(null); setLedgerDetailError(null); }}
      />
      {ledgerDetailError && (
        <div className="owner-alert owner-alert-error" style={{ position: 'fixed', bottom: 16, left: 16, right: 16, maxWidth: 400, zIndex: 9999 }} role="alert">
          {ledgerDetailError}
          <button type="button" onClick={() => setLedgerDetailError(null)} style={{ marginLeft: 8 }}>Dismiss</button>
        </div>
      )}
      {isMobile && (
        <FinancialsFiltersSheet
          isOpen={filtersSheetOpen}
          onClose={() => setFiltersSheetOpen(false)}
          searchQuery={ledgerSearchQuery}
          onSearchChange={setLedgerSearchQuery}
          exportError={exportError}
          onExportExcel={() => { setExportError(null); handleExportXlsx(); }}
        />
      )}
    </OwnerPageChrome>
  );
}

const styles = {
  // Ledger Sheet - White working surface; width/corners match scorecard container (max 1600px, rounded)
  // Width and borderRadius must match scorecards exactly (maxWidth 1600px, borderRadius matches --owner-card-radius)
  // Padding is handled by contentWidthStyle on children, not here (to avoid double padding)
  ledgerSheet: {
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    borderRadius: 'var(--owner-card-radius, 12px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    position: 'relative',
    marginTop: '6px',
    minHeight: 'calc(100vh - 300px)',
    maxWidth: '1600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    overflow: 'visible',
  },
  ledgerHeaderLayout: {
    position: 'sticky',
    top: 'var(--owner-top-nav-height, 52px)',
    zIndex: 90,
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    borderBottom: '2px solid var(--color-border-medium, #d1d5db)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    display: 'grid',
    gridTemplateRows: 'auto auto',
    gridTemplateAreas: '"period" "tabs"',
    gap: 'var(--spacing-2, 8px)',
    maxWidth: '1600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    padding: '6px var(--spacing-4, 16px)',
  },
  ledgerHeaderRow1: {
    gridArea: 'period',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--spacing-3, 12px)',
    flexWrap: 'wrap',
    minHeight: '32px',
    paddingTop: 0,
    paddingBottom: '6px',
    borderBottom: '1px solid var(--color-border-light, #e5e7eb)',
  },
  ledgerHeaderRow1Left: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-3, 12px)',
    flexWrap: 'wrap',
    minWidth: 0,
    lineHeight: 1.4,
  },
  ledgerHeaderRow1Right: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2, 8px)',
    flexShrink: 0,
    lineHeight: 1.4,
  },
  ledgerHeaderRow2: {
    gridArea: 'tabs',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--spacing-3, 12px)',
    flexWrap: 'wrap',
    minHeight: '40px',
    paddingTop: 0,
  },
  ledgerHeaderRow2Right: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 'var(--spacing-2, 8px)',
    flexShrink: 0,
  },
  dateRangeDisplay: {
    fontSize: 'var(--font-size-base, 16px)',
    color: 'var(--color-text-secondary, #6b7280)',
    fontWeight: 'var(--font-weight-medium, 500)',
    whiteSpace: 'nowrap',
    lineHeight: 1.4,
    display: 'flex',
    alignItems: 'center',
  },
  viewLabel: {
    fontSize: 'var(--font-size-sm, 14px)',
    fontWeight: 'var(--font-weight-medium, 500)',
    color: 'var(--color-text-secondary, #6b7280)',
    whiteSpace: 'nowrap',
    lineHeight: 1.4,
  },
  viewSelect: {
    padding: 'var(--spacing-2, 8px) var(--spacing-3, 12px)',
    fontSize: 'var(--font-size-sm, 14px)',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md, 6px)',
    backgroundColor: 'var(--color-bg-primary, #fff)',
    cursor: 'pointer',
    lineHeight: 1.4,
  },
  presetLabel: {
    fontSize: 'var(--font-size-sm, 14px)',
    color: 'var(--color-text-secondary, #6b7280)',
    whiteSpace: 'nowrap',
  },
  presetSelect: {
    padding: '6px 10px',
    fontSize: 'var(--font-size-sm, 14px)',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md, 6px)',
    backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
    cursor: 'pointer',
  },
  columnsButton: {
    padding: 'var(--spacing-2, 8px) var(--spacing-3, 12px)',
    fontSize: 'var(--font-size-sm, 14px)',
    fontWeight: 'var(--font-weight-medium, 500)',
    color: 'var(--color-text-primary, #111827)',
    backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md, 6px)',
    cursor: 'pointer',
  },
  columnPickerDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    minWidth: 180,
    background: 'var(--color-bg-primary, #fff)',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md, 6px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    zIndex: 1000,
    padding: 'var(--spacing-2, 8px)',
  },
  columnPickerLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    padding: '4px 0',
  },
  filtersButton: {
    padding: '8px 14px',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    minHeight: '44px',
  },
  periodButtonMobile: {
    padding: '8px 12px',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--color-text-primary)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    textAlign: 'left',
    minHeight: '44px',
    width: '100%',
    maxWidth: '100%',
  },
  tabContentContainer: {
    maxWidth: '1600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    paddingLeft: 'var(--spacing-4, 16px)',
    paddingRight: 'var(--spacing-4, 16px)',
  },
  tabContent: {
    paddingTop: 'var(--spacing-4, 16px)',
    paddingBottom: 'var(--spacing-6, 24px)',
  },
  paymentsTaxesScrollArea: {
    overflow: 'visible',
    /* Table expands naturally; page scrolls (no inner vertical scroll trap) */
  },
  paymentsTaxesTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--spacing-4)',
    marginBottom: 'var(--spacing-3)',
    flexWrap: 'wrap',
    flexShrink: 0,
  },
  paymentsTaxesSummaryToggle: {
    padding: 'var(--spacing-1, 4px) var(--spacing-2, 8px)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    background: 'transparent',
    border: '1px solid var(--color-border-medium)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  paymentsTaxesSummaryCollapse: {
    overflow: 'hidden',
    transition: 'max-height 0.3s ease, opacity 0.2s ease',
  },
  paymentsTaxesHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  paymentsTaxesCheckboxLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
  },
  paymentsTaxesGuestButton: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: 'var(--spacing-2, 8px) var(--spacing-3, 12px)',
    margin: 'calc(-1 * var(--spacing-2, 8px)) calc(-1 * var(--spacing-3, 12px))',
    boxSizing: 'content-box',
    minHeight: '1.5em',
    background: 'none',
    border: 'none',
    font: 'inherit',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  paymentsTaxesThHierarchy: {
    backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
    fontWeight: 700,
    letterSpacing: '0.02em',
    boxShadow: '0 2px 0 0 var(--color-border-medium, #d1d5db)',
  },
  paymentsTaxesSection: {
    marginBottom: 'var(--spacing-6, 24px)',
    minHeight: 0,
    overflow: 'visible',
    paddingTop: 'var(--spacing-3, 12px)',
  },
  paymentsTaxesContentWrap: {
    paddingTop: 'var(--spacing-2, 8px)',
    paddingBottom: 'var(--spacing-4, 16px)',
    minHeight: 0,
  },
  paymentsTaxesErrorWrap: {
    paddingTop: 'var(--spacing-4, 16px)',
    paddingBottom: 'var(--spacing-4, 16px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 120,
  },
  paymentsTaxesCardTitle: {
    fontSize: 'var(--font-size-xl, 20px)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
    marginBottom: 'var(--spacing-4, 16px)',
    marginTop: 0,
    lineHeight: 1.3,
    overflow: 'visible',
  },
  paymentsTaxesSummaryStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 'var(--spacing-4, 16px)',
    marginBottom: 'var(--spacing-2, 8px)',
    paddingBottom: 'var(--spacing-2, 8px)',
  },
  paymentsTaxesSummaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1, 4px)',
  },
  paymentsTaxesSummaryLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-1, 4px)',
    fontSize: 'var(--font-size-sm, 14px)',
    color: 'var(--color-text-secondary, #6b7280)',
    fontWeight: 'var(--font-weight-medium, 500)',
  },
  paymentsTaxesSummaryValue: {
    fontSize: 'var(--font-size-lg, 18px)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
  },
  paymentsTaxesHelperCopy: {
    fontSize: 'var(--font-size-sm, 14px)',
    color: 'var(--color-text-secondary, #6b7280)',
    fontStyle: 'italic',
    marginTop: 'var(--spacing-2, 8px)',
    marginBottom: 'var(--spacing-2, 8px)',
    marginLeft: 0,
    marginRight: 0,
  },
  paymentsTaxesTableScrollWrap: {
    marginTop: 'var(--spacing-4, 16px)',
    overflowX: 'auto',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md, 6px)',
  },
  paymentsTaxesTableWrap: {
    minWidth: '100%',
  },
  paymentsTaxesTable: {
    width: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: 'var(--font-size-sm, 14px)',
  },
  paymentsTaxesTheadSticky: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
  },
  paymentsTaxesThSticky: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
    boxShadow: '0 2px 0 0 var(--color-border-medium, #d1d5db)',
  },
  paymentsTaxesTh: {
    textAlign: 'left',
    padding: 'var(--spacing-2, 8px) var(--spacing-3, 12px)',
    borderBottom: '2px solid var(--color-border-medium, #d1d5db)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-secondary, #6b7280)',
  },
  paymentsTaxesThInner: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-1, 4px)',
  },
  paymentsTaxesThNumeric: {
    textAlign: 'right',
    padding: 'var(--spacing-2, 8px) var(--spacing-3, 12px)',
    borderBottom: '2px solid var(--color-border-medium, #d1d5db)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-secondary, #6b7280)',
  },
  paymentsTaxesTd: {
    padding: 'var(--spacing-2, 8px) var(--spacing-3, 12px)',
    borderBottom: '1px solid var(--color-border-light, #e5e7eb)',
    color: 'var(--color-text-primary, #111827)',
  },
  paymentsTaxesTdNumeric: {
    padding: 'var(--spacing-2, 8px) var(--spacing-3, 12px)',
    borderBottom: '1px solid var(--color-border-light, #e5e7eb)',
    textAlign: 'right',
    color: 'var(--color-text-primary, #111827)',
  },
  paymentsTaxesTotalsRow: {
    backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
    fontWeight: 'var(--font-weight-semibold, 600)',
  },
  paymentsTaxesSectionTitle: {
    fontSize: 'var(--font-size-lg, 18px)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
    marginBottom: 'var(--spacing-3, 12px)',
    marginTop: 0,
  },
  overviewCard: {
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md, 6px)',
    padding: 'var(--spacing-4, 16px)',
  },
  overviewTitle: {
    fontSize: 'var(--font-size-xl, 20px)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
    marginBottom: 'var(--spacing-4, 16px)',
  },
  overviewHero: {
    marginBottom: 'var(--spacing-5, 20px)',
    paddingBottom: 'var(--spacing-4, 16px)',
    borderBottom: '1px solid var(--color-border-light, #e5e7eb)',
  },
  overviewHeroLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-1, 4px)',
    marginBottom: 'var(--spacing-1, 4px)',
  },
  overviewHeroLabel: {
    fontSize: 'var(--font-size-sm, 14px)',
    color: 'var(--color-text-secondary, #6b7280)',
    fontWeight: 'var(--font-weight-medium, 500)',
  },
  overviewTooltipIcon: {
    marginLeft: 'var(--spacing-1, 4px)',
    opacity: 0.8,
    fontSize: 'var(--font-size-xs, 12px)',
    cursor: 'help',
  },
  overviewHeroValue: {
    fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
    fontWeight: 700,
    color: 'var(--color-text-primary, #111827)',
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    background: 'linear-gradient(135deg, var(--color-text-primary, #111827) 0%, #374151 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  overviewHeroSubtext: {
    fontSize: 'var(--font-size-sm, 14px)',
    color: 'var(--color-text-secondary, #6b7280)',
    marginTop: 'var(--spacing-1, 4px)',
  },
  overviewPillars: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 'var(--spacing-4, 16px)',
    marginBottom: 'var(--spacing-4, 16px)',
  },
  overviewPillarItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1, 4px)',
  },
  overviewPillarLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-1, 4px)',
    fontSize: 'var(--font-size-sm, 14px)',
    color: 'var(--color-text-secondary, #6b7280)',
    fontWeight: 'var(--font-weight-medium, 500)',
  },
  overviewPillarValue: {
    fontSize: 'var(--font-size-lg, 18px)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-text-primary, #111827)',
  },
  overviewContext: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 'var(--spacing-3, 12px)',
  },
  overviewContextItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1, 4px)',
  },
  overviewContextLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-1, 4px)',
    fontSize: 'var(--font-size-xs, 12px)',
    color: 'var(--color-text-secondary, #6b7280)',
    fontWeight: 500,
  },
  overviewContextValue: {
    fontSize: 'var(--font-size-base, 16px)',
    fontWeight: 600,
    color: 'var(--color-text-primary, #111827)',
  },
  container: {
    padding: 'var(--spacing-4, 16px)',
    maxWidth: '1600px',
    margin: '0 auto',
  },
  header: {
    marginBottom: 'var(--spacing-6)',
  },
  title: {
    fontSize: 'var(--font-size-2xl)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    margin: 0,
  },
  section: {
    marginBottom: 'var(--spacing-8)',
  },
  sectionTitle: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    marginBottom: 'var(--spacing-4)',
  },
  // Removed unused styles from old implementation
};

